import {
  ALLOWED_TAGS, DROPPED_SUBTREES, IMPLIED_END, VOID_TAGS,
  cleanAttributes, decodeEntities, escapeAttribute, escapeMarkupText, type SafeAttribute
} from './htmlPolicy';

// A tokeniser that re-serialises markup against `htmlPolicy`, rather than a blacklist that deletes
// the shapes somebody thought of. Nothing is copied through: every tag, attribute and character in
// the output was written by this file, from a name the policy named.
//
// The settings grammar refuses `<script`, `javascript:`, `data:text/html` and `on*=` when a merchant
// saves. It does not know about `<iframe srcdoc>`, `<object>`, `<embed>` or `<svg onbegin>`, so this
// is the line those four stop at — see `htmlSanitize.test.ts`, which fires all four at it.

/** The longest markup one element may draw; the settings grammar caps a leaf at 5000, this is the backstop. */
const MAX_HTML = 20000;

/** How many attributes one tag may carry, and how deep the output may nest, so neither can be made unbounded. */
const MAX_ATTRIBUTES = 40;
const MAX_OPEN_DEPTH = 64;

/** What the walk is building: the markup written so far, and the tags still waiting to be closed. */
type Sink = { out: string[]; open: string[] };

/** One start tag as it was read, before the policy has said anything about it. */
type StartTag = { name: string; attrs: SafeAttribute[]; selfClosing: boolean; end: number };

const TAG_NAME_RE = /<([a-zA-Z][a-zA-Z0-9:_.-]*)/y;
const END_TAG_RE = /<\/\s*([a-zA-Z][a-zA-Z0-9:_.-]*)[^>]*>?/y;
const SPACE_RE = /\s*/y;
const ATTR_RE = /([^\s/>="'<]+)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s>]*))?/y;

// Rewrites merchant markup as the subset of it the policy allows, and returns markup this file wrote.
export function sanitizeElementHtml(raw: unknown): string {
  if (typeof raw !== 'string' || raw === '') return '';
  const html = raw.length > MAX_HTML ? raw.slice(0, MAX_HTML) : raw;
  const sink: Sink = { out: [], open: [] };
  let at = 0;
  while (at < html.length) {
    const next = html.indexOf('<', at);
    if (next === -1) { sink.out.push(escapeMarkupText(html.slice(at))); break; }
    if (next > at) sink.out.push(escapeMarkupText(html.slice(at, next)));
    at = consumeMarkup(html, next, sink);
  }
  while (sink.open.length > 0) sink.out.push(`</${sink.open.pop()}>`);
  return sink.out.join('');
}

// Consumes whatever the `<` at this point begins, and reports where the next thing starts.
function consumeMarkup(html: string, at: number, sink: Sink): number {
  if (html.startsWith('<!--', at)) return skipPast(html, at + 4, '-->');
  if (html.startsWith('<!', at) || html.startsWith('<?', at)) return skipPast(html, at + 2, '>');
  if (html.startsWith('</', at)) return consumeEndTag(html, at, sink);
  const tag = readStartTag(html, at);
  if (!tag) { sink.out.push('&lt;'); return at + 1; }
  if (DROPPED_SUBTREES.has(tag.name)) return dropTag(html, tag);
  if (ALLOWED_TAGS.has(tag.name)) openTag(tag, sink);
  return tag.end;
}

// Skips a comment or a doctype, which are markup for the parser rather than content for the page.
function skipPast(html: string, from: number, marker: string): number {
  const end = html.indexOf(marker, from);
  return end === -1 ? html.length : end + marker.length;
}

// Drops a forbidden tag, and the payload inside it when it is the kind of tag that has one.
function dropTag(html: string, tag: StartTag): number {
  if (tag.selfClosing || VOID_TAGS.has(tag.name)) return tag.end;
  return skipSubtree(html, tag.end, tag.name);
}

// Closes the tag an end tag names, and every tag the merchant left open inside it.
function consumeEndTag(html: string, at: number, sink: Sink): number {
  END_TAG_RE.lastIndex = at;
  const match = END_TAG_RE.exec(html);
  if (!match) { sink.out.push('&lt;'); return at + 1; }
  const index = sink.open.lastIndexOf(localName(match[1]));
  while (index !== -1 && sink.open.length > index) sink.out.push(`</${sink.open.pop()}>`);
  return END_TAG_RE.lastIndex;
}

// Writes one allowed tag with the attributes the policy kept, and remembers it needs closing.
function openTag(tag: StartTag, sink: Sink): void {
  const written = cleanAttributes(tag.name, tag.attrs)
    .map(({ name, value }) => ` ${name}="${escapeAttribute(decodeEntities(value))}"`).join('');
  if (VOID_TAGS.has(tag.name)) { sink.out.push(`<${tag.name}${written} />`); return; }
  closeImplied(tag.name, sink);
  if (sink.open.length >= MAX_OPEN_DEPTH) return;
  sink.out.push(`<${tag.name}${written}>`);
  sink.open.push(tag.name);
}

// Closes what a start tag implies the end of, the way a browser does with a `<p>` a merchant left open.
function closeImplied(name: string, sink: Sink): void {
  const implied = IMPLIED_END[name];
  if (!implied) return;
  while (sink.open.length > 0 && implied.includes(sink.open[sink.open.length - 1])) {
    sink.out.push(`</${sink.open.pop()}>`);
  }
}

// Reads the name and attributes of a start tag, or nothing when the `<` was only a `<` the merchant typed.
function readStartTag(html: string, at: number): StartTag | null {
  TAG_NAME_RE.lastIndex = at;
  const match = TAG_NAME_RE.exec(html);
  if (!match) return null;
  const tail = readAttributes(html, TAG_NAME_RE.lastIndex);
  return { name: localName(match[1]), attrs: tail.attrs, selfClosing: tail.selfClosing, end: tail.end };
}

/** Everything after a tag's name: what it carried, whether it closed itself, and where it ended. */
type TagTail = { attrs: SafeAttribute[]; selfClosing: boolean; end: number };

// Reads the attributes of one start tag, stopping at its `>` or at the end of the markup.
function readAttributes(html: string, from: number): TagTail {
  const attrs: SafeAttribute[] = [];
  let at = from;
  let selfClosing = false;
  while (at < html.length && attrs.length < MAX_ATTRIBUTES) {
    SPACE_RE.lastIndex = at;
    if (SPACE_RE.exec(html)) at = SPACE_RE.lastIndex;
    if (html[at] === '>') return { attrs, selfClosing, end: at + 1 };
    if (html[at] === '/') { selfClosing = true; at += 1; continue; }
    ATTR_RE.lastIndex = at;
    const match = ATTR_RE.exec(html);
    if (!match) return { attrs, selfClosing, end: at + 1 };
    attrs.push({ name: match[1].toLowerCase(), value: unquote(match[2]) });
    at = ATTR_RE.lastIndex;
    selfClosing = false;
  }
  return { attrs, selfClosing, end: skipPast(html, at, '>') };
}

// Reads an attribute's value out of whichever quoting the merchant used, or none at all.
function unquote(raw: string | undefined): string {
  if (!raw) return '';
  const quoted = (raw[0] === '"' || raw[0] === "'") && raw[raw.length - 1] === raw[0];
  return quoted ? raw.slice(1, -1) : raw;
}

// Skips a dropped element and everything inside it, so `<iframe srcdoc="…">` takes its payload with it.
function skipSubtree(html: string, from: number, name: string): number {
  const lower = html.toLowerCase();
  let depth = 1;
  let at = from;
  while (depth > 0) {
    const close = findTag(lower, at, `</${name}`);
    if (close === -1) return html.length;
    const open = findTag(lower, at, `<${name}`);
    if (open !== -1 && open < close) { depth += 1; at = open + name.length + 1; continue; }
    depth -= 1;
    at = close + name.length + 2;
  }
  return skipPast(html, at, '>');
}

// Finds the next occurrence of a tag opener that is that tag, rather than a longer name starting the same way.
function findTag(lower: string, from: number, marker: string): number {
  for (let at = lower.indexOf(marker, from); at !== -1; at = lower.indexOf(marker, at + 1)) {
    const after = lower[at + marker.length] ?? '>';
    if (after === '>' || after === '/' || after.trim() === '') return at;
  }
  return -1;
}

// Reduces a namespaced tag to the name that decides its policy, so `<svg:script>` is judged as `script`.
function localName(raw: string): string {
  const lower = raw.toLowerCase();
  const colon = lower.lastIndexOf(':');
  return colon === -1 ? lower : lower.slice(colon + 1);
}
